import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import {
  FiFilm, FiImage, FiLoader, FiPlus, FiTrash2, FiType, FiUpload, FiVideo, FiX,
} from 'react-icons/fi';
import { cCreateStory, cUpload, cUploadVideo, fullUrl } from '../../api';
import { useLanguage } from '../../i18n';
import { readImageFile } from '../../utils/audio';
import { formatBytes, videoError } from '../../utils/media';
import { parseVideo } from '../../utils/video';
import ImageCropper from './ImageCropper';
import { BLUE } from './brand';

const BACKGROUNDS = [
  'linear-gradient(135deg, #0B5FDB 0%, #083E93 100%)',
  'linear-gradient(135deg, #FB6E08 0%, #b45309 100%)',
  'linear-gradient(135deg, #0B5FDB 0%, #FB6E08 100%)',
  'linear-gradient(135deg, #111827 0%, #374151 100%)',
  'linear-gradient(135deg, #047857 0%, #065f46 100%)',
  'linear-gradient(135deg, #7c3aed 0%, #4c1d95 100%)',
];

/** "Add a story" modal — photo / GIF upload or link, uploaded or linked video, or text card. */
export default function StoryComposer({ onClose, onSaved }) {
  const { t } = useLanguage();
  const [kind, setKind] = useState('image');
  const [media, setMedia] = useState('');
  const [text, setText] = useState('');
  const [background, setBackground] = useState(BACKGROUNDS[0]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [crop, setCrop] = useState(null);        // image being cropped / formatted
  const [videoStory, setVideoStory] = useState(null);   // uploaded story video
  const [videoBusy, setVideoBusy] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await cUpload([file]);
      setMedia(res.urls?.[0] || '');
      setKind('image');
      toast.success(t('uploaded') || 'OK');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /** The user picked a file → open the editor first (crop / format / rotate). */
  const openEditor = async (file) => {
    if (!file) return;
    if ((file.type || '').includes('gif')) {
      setCrop(null);
      await upload(file);            // GIFs keep their animation (no cropping)
      toast(t('gifNoCrop'), { icon: '🎞️' });
      return;
    }
    try {
      const dataUrl = await readImageFile(file);
      setCrop(dataUrl);
    } catch (e) {
      await upload(file);
    }
  };

  /** Upload a story video (≤ 10 MB) — compressed to MP4 with a poster frame. */
  const pickVideoFile = async (file) => {
    if (!file) return;
    const bad = videoError(file);
    if (bad) { toast.error(bad); return; }
    setVideoBusy(true);
    try {
      const res = await cUploadVideo(file);
      setVideoStory(res);
      setKind('video_file');
      setMedia('');
      toast.success(`${t('videoUploaded')} · ${formatBytes(res.original_size)} → ${formatBytes(res.size)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setVideoBusy(false);
    }
  };

  /** Cropped/rotated JPEG came back from the editor → upload it. */
  const cropped = async (file) => {
    setCrop(null);
    await upload(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (kind === 'text' && !text.trim()) { toast.error(t('writeSomething')); return; }
    if (kind === 'video_file' && !videoStory) { toast.error(t('uploadVideo')); return; }
    if (kind !== 'text' && kind !== 'video_file' && !media.trim()) { toast.error(t('storyPhotoUrl')); return; }
    setBusy(true);
    try {
      await cCreateStory({
        kind,
        media: kind === 'video_file' ? videoStory.url : media.trim(),
        thumb: kind === 'video_file' ? (videoStory.thumb || '') : '',
        text: text.trim(),
        background,
      });
      toast.success(t('storyPosted'));
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };

  const tab = (key, icon, label) => {
    const on = kind === key || (key === 'video' && kind === 'video_file');
    return (
      <button
        key={key}
        type="button"
        onClick={() => setKind(key === 'video' && videoStory ? 'video_file' : key)}
        className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold border ${
          on ? 'text-white' : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
        }`}
        style={on ? { background: BLUE, borderColor: BLUE } : undefined}
      >
        {icon} {label}
      </button>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm anim-fade-in" onClick={onClose} />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md rounded-3xl bg-white dark:bg-gray-900 shadow-2xl anim-modal overflow-hidden"
      >
        <header className="flex items-center justify-between px-5 pt-4 pb-2">
          <h3 className="font-extrabold text-base dark:text-gray-100">{t('createStory')}</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label={t('close')}>
            <FiX className="w-5 h-5" />
          </button>
        </header>

        <div className="px-5 pb-5 space-y-3">
          <div className="flex items-center gap-2">
            {tab('image', <FiImage className="w-4 h-4" />, t('storyPhotoUrl'))}
            {tab('video', videoStory ? <FiFilm className="w-4 h-4" /> : <FiVideo className="w-4 h-4" />,
              videoStory ? t('videoStory') : t('stories'))}
            {tab('text', <FiType className="w-4 h-4" />, t('storyTextLabel'))}
          </div>

          {kind === 'image' && (
            <>
              <input
                value={media}
                onChange={(e) => setMedia(e.target.value)}
                placeholder="https://…/photo.jpg"
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm"
              />
              <button
                type="button"
                onClick={() => fileRef.current && fileRef.current.click()}
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-sm font-semibold text-gray-600 dark:text-gray-300"
              >
                {uploading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiUpload className="w-4 h-4" />}
                {uploading ? t('uploading') : t('uploadImage')}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/*"
                hidden
                onChange={(e) => { openEditor(e.target.files?.[0]); if (fileRef.current) fileRef.current.value = ''; }}
              />

              {/* crop / format / rotate before the story is uploaded */}
              {crop && (
                <ImageCropper
                  src={crop}
                  ratio="story"
                  onDone={cropped}
                  onCancel={() => setCrop(null)}
                />
              )}
              {media.trim() && (
                <span className="block relative rounded-2xl overflow-hidden" style={{ aspectRatio: '9 / 16', maxHeight: 260 }}>
                  <img src={/^https?:/i.test(media) ? media : fullUrl(media)} alt="story" className="w-full h-full object-cover" />
                </span>
              )}
            </>
          )}

          {kind === 'video' && (
            <>
              <input
                value={media}
                onChange={(e) => { setMedia(e.target.value); setVideoStory(null); }}
                placeholder="https://www.facebook.com/reel/… · YouTube · TikTok"
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm"
              />
              {media.trim() && (
                <p className="text-[11px] text-gray-500">
                  {(() => {
                    const v = parseVideo(media);
                    return `${v.type} · ${v.ratioKey}`;
                  })()}
                </p>
              )}
            </>
          )}

          {(kind === 'video' || kind === 'video_file') && (
            <>
              <div className="flex items-center gap-2">
                <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                <span className="text-[10px] font-bold text-gray-400 uppercase">{t('orUpload')}</span>
                <span className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              </div>

              <button
                type="button"
                onClick={() => videoRef.current && videoRef.current.click()}
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-sm font-semibold text-gray-600 dark:text-gray-300"
              >
                {videoBusy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiFilm className="w-4 h-4" />}
                {videoBusy ? t('uploading') : t('uploadVideo10')}
              </button>
              <input
                ref={videoRef}
                type="file"
                accept="video/*"
                hidden
                onChange={(e) => { pickVideoFile(e.target.files?.[0]); if (videoRef.current) videoRef.current.value = ''; }}
              />
              <p className="text-[11px] text-gray-400">{t('videoCompressHint')}</p>

              {videoStory && (
                <div className="relative rounded-2xl overflow-hidden bg-black" style={{ maxHeight: 260 }}>
                  <video
                    src={videoStory.url.startsWith('http') ? videoStory.url : fullUrl(videoStory.url)}
                    poster={videoStory.thumb ? fullUrl(videoStory.thumb) : undefined}
                    controls
                    muted
                    playsInline
                    className="w-full"
                    style={{ maxHeight: 260, objectFit: 'contain' }}
                  />
                  <button
                    type="button"
                    onClick={() => { setVideoStory(null); }}
                    className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-black/60 text-white text-[11px] font-bold"
                  >
                    <FiTrash2 className="w-3.5 h-3.5" /> {t('remove')}
                  </button>
                  {videoStory.size > 0 && (
                    <span className="absolute bottom-2 left-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/60 text-white">
                      {formatBytes(videoStory.size)}
                    </span>
                  )}
                </div>
              )}
            </>
          )}

          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={kind === 'text' ? 4 : 2}
            placeholder={kind === 'text' ? t('storyTextLabel') : t('postDescription')}
            className={`w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm ${
              kind === 'text' ? 'text-white placeholder-white/70 font-bold' : ''
            }`}
            style={kind === 'text' ? { background } : undefined}
          />

          {kind === 'text' && (
            <div className="flex items-center gap-2 flex-wrap">
              {BACKGROUNDS.map((bg) => (
                <button
                  key={bg}
                  type="button"
                  onClick={() => setBackground(bg)}
                  className={`w-8 h-8 rounded-full border-2 ${background === bg ? 'border-gray-900 dark:border-white' : 'border-transparent'}`}
                  style={{ background: bg }}
                  aria-label={t('storyBackground')}
                />
              ))}
              <button type="button" onClick={() => setText('')} className="ml-auto inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-500">
                <FiTrash2 className="w-3.5 h-3.5" /> {t('remove')}
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || uploading}
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl text-white font-bold text-sm disabled:opacity-60"
            style={{ background: BLUE }}
          >
            {busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />}
            {t('createStory')}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
